import { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import {
  SlidersHorizontal, Plus, Loader2,
  ArrowDownCircle, ArrowUpCircle, Clock, CheckCircle2, XCircle,
  ChevronDown, ChevronUp, Search, User,
} from "lucide-react";
import { useAuth } from "../../../hooks/useAuth";
import { useAdjustments, useCreateAdjustment, useCancelAdjustment } from "../../../hooks/useAdjustment";
import { useBizUsers } from "../../../hooks/useBizAdmin";
import { Modal } from "../../../components/ui/Modal";
import { Button } from "../../../components/ui/Button";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import type { PayslipAdjustment, AdjustmentStatus } from "../../../lib/adjustmentApi";

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCurrency(n: number) {
  return `₱${n.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPeriod(period: string) {
  const m = period.match(/^(\d{4})-(\d{2})-(H[12])$/);
  if (!m) return period;
  const [, year, month, half] = m;
  const d = new Date(parseInt(year!), parseInt(month!) - 1, 1);
  const name = d.toLocaleDateString("en-US", { month: "long" });
  return half === "H1" ? `${name} ${year} (1–15)` : `${name} ${year} (16–EOM)`;
}

function generatePeriods(count = 6): string[] {
  const periods: string[] = [];
  const now = new Date();
  let year = now.getFullYear();
  let month = now.getMonth() + 1;
  let half: "H1" | "H2" = now.getDate() <= 15 ? "H1" : "H2";
  for (let i = 0; i < count; i++) {
    periods.push(`${year}-${String(month).padStart(2, "0")}-${half}`);
    if (half === "H2") { half = "H1"; }
    else { half = "H2"; if (month === 1) { year--; month = 12; } else { month--; } }
  }
  const seen = new Set<string>();
  return periods.filter((p) => { if (seen.has(p)) return false; seen.add(p); return true; });
}

const RECENT_PERIODS = generatePeriods(6);

const STATUS_CONFIG: Record<AdjustmentStatus, { label: string; badge: string; border: string; icon: typeof Clock }> = {
  PENDING:   { label: "Pending",   badge: "bg-[#fef9c3] text-[#a16207]", border: "border-l-[#f59e0b]", icon: Clock },
  APPLIED:   { label: "Applied",   badge: "bg-[#dcfce7] text-[#15803d]", border: "border-l-[#16a34a]", icon: CheckCircle2 },
  CANCELLED: { label: "Cancelled", badge: "bg-[#fee2e2] text-[#b91c1c]", border: "border-l-[#dc2626]", icon: XCircle },
};

const INPUT_CLASS = "w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20";
const LABEL_CLASS = "block text-xs font-medium text-[#374151] mb-1";
const ERROR_CLASS = "text-xs text-[#dc2626] mt-1";

// ── Employee Combobox ─────────────────────────────────────────────────────────

interface Employee { userId: string; firstName?: string; lastName?: string; username: string; }

function EmployeeCombobox({ businessId, value, onChange, error }: {
  businessId: string; value: string;
  onChange: (userId: string, name: string) => void; error?: string;
}) {
  const { data: employees = [] } = useBizUsers(businessId);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (!value) { setDisplayName(""); setQuery(""); return; }
    const emp = employees.find((e) => e.userId === value);
    if (emp) {
      const name = [emp.firstName, emp.lastName].filter(Boolean).join(" ") || emp.username;
      setDisplayName(name); setQuery(name);
    }
  }, [value, employees]);

  const filtered = employees.filter((e) => {
    if (!query) return true;
    const q = query.toLowerCase();
    const name = [e.firstName, e.lastName].filter(Boolean).join(" ").toLowerCase();
    return name.includes(q) || e.username.toLowerCase().includes(q);
  });

  const handleSelect = (emp: Employee) => {
    const name = [emp.firstName, emp.lastName].filter(Boolean).join(" ") || emp.username;
    setDisplayName(name); setQuery(name); setOpen(false);
    onChange(emp.userId, name);
  };

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#94a3b8] pointer-events-none" />
        <input
          type="text" autoComplete="off" placeholder="Search employee…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          className={`${INPUT_CLASS} pl-9 ${error ? "border-[#dc2626]" : ""}`}
        />
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-[#e2e8f0] rounded-xl shadow-[0_4px_16px_0_rgb(0_0_0/0.12)] max-h-52 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="px-3 py-3 text-xs text-[#94a3b8] text-center">No employees found</p>
          ) : (
            filtered.map((emp) => {
              const name = [emp.firstName, emp.lastName].filter(Boolean).join(" ") || emp.username;
              return (
                <button key={emp.userId} type="button"
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(emp); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-[#f8fafc] transition-colors"
                >
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#eff3ff]">
                    <User className="h-3.5 w-3.5 text-[#2447d9]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#0f172a] truncate">{name}</p>
                    <p className="text-xs text-[#94a3b8] truncate">{emp.username}</p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
      {value && displayName && (
        <p className="mt-1 text-[10px] text-[#94a3b8] font-mono truncate">ID: {value}</p>
      )}
      {error && <p className={ERROR_CLASS}>{error}</p>}
    </div>
  );
}

// ── Create Adjustment Modal ───────────────────────────────────────────────────

const createSchema = z.object({
  userId: z.string().min(1, "Select an employee"),
  targetPeriod: z.string().min(1, "Select a period"),
  type: z.enum(["DEDUCTION", "ADDITION"] as const),
  label: z.string().min(1, "Label is required").max(200),
  amount: z.number({ invalid_type_error: "Enter a valid amount" }).positive("Must be greater than 0"),
  reason: z.string().max(500).optional(),
  sourceDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD").optional().or(z.literal("")),
});
type CreateForm = z.infer<typeof createSchema>;

function CreateAdjustmentModal({ businessId, defaultPeriod, onClose }: {
  businessId: string; defaultPeriod: string; onClose: () => void;
}) {
  const mutation = useCreateAdjustment(businessId);
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: { targetPeriod: defaultPeriod, type: "DEDUCTION" },
  });
  const userId = watch("userId");

  const onSubmit = (data: CreateForm) => {
    mutation.mutate(
      { userId: data.userId, targetPeriod: data.targetPeriod, type: data.type, label: data.label, amount: data.amount, reason: data.reason || undefined, sourceDate: data.sourceDate || undefined },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal isOpen onClose={onClose} title="New Payslip Adjustment" size="md">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLASS}>Employee</label>
            <EmployeeCombobox
              businessId={businessId} value={userId ?? ""}
              onChange={(id) => setValue("userId", id, { shouldValidate: true })}
              error={errors.userId?.message}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Target Period</label>
            <select className={INPUT_CLASS} {...register("targetPeriod")}>
              {RECENT_PERIODS.map((p) => <option key={p} value={p}>{formatPeriod(p)}</option>)}
            </select>
            {errors.targetPeriod && <p className={ERROR_CLASS}>{errors.targetPeriod.message}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLASS}>Type</label>
            <select className={INPUT_CLASS} {...register("type")}>
              <option value="DEDUCTION">Deduction (reduces pay)</option>
              <option value="ADDITION">Addition (increases pay)</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLASS}>Amount (₱)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">₱</span>
              <input type="number" step="0.01" min="0.01" placeholder="0.00"
                className={`${INPUT_CLASS} pl-7`}
                {...register("amount", { valueAsNumber: true })} />
            </div>
            {errors.amount && <p className={ERROR_CLASS}>{errors.amount.message}</p>}
          </div>
        </div>

        <div>
          <label className={LABEL_CLASS}>Label</label>
          <input type="text" placeholder="e.g. PPA – Absent Jul 14" className={INPUT_CLASS} {...register("label")} />
          {errors.label && <p className={ERROR_CLASS}>{errors.label.message}</p>}
        </div>

        <div>
          <label className={LABEL_CLASS}>
            Event Date <span className="text-[#94a3b8] font-normal">(optional)</span>
          </label>
          <input type="date" className={INPUT_CLASS} {...register("sourceDate")} />
          <p className="mt-1 text-[11px] text-[#94a3b8]">The date the event occurred (e.g. the absent day)</p>
        </div>

        <div>
          <label className={LABEL_CLASS}>
            Reason <span className="text-[#94a3b8] font-normal">(optional)</span>
          </label>
          <textarea rows={2} placeholder="Brief explanation..." className={INPUT_CLASS + " resize-none"} {...register("reason")} />
        </div>

        {mutation.isError && (
          <p className="rounded-lg bg-[#fef2f2] px-3 py-2 text-sm text-[#dc2626]">Something went wrong. Please try again.</p>
        )}

        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <button type="submit" disabled={mutation.isPending}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3a8a] disabled:opacity-60 disabled:cursor-not-allowed transition-colors">
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Create Adjustment
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Adjustment Row ────────────────────────────────────────────────────────────

function AdjustmentRow({ adj, businessId }: { adj: PayslipAdjustment; businessId: string }) {
  const [expanded, setExpanded] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const cancel = useCancelAdjustment(businessId);

  const cfg = STATUS_CONFIG[adj.status];
  const isDeduction = adj.type === "DEDUCTION";

  return (
    <div className={`rounded-xl border border-[#e2e8f0] border-l-4 ${cfg.border} bg-white overflow-hidden shadow-sm`}>
      <div className="px-5 py-4 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          {/* Type icon */}
          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${isDeduction ? "bg-[#fef2f2]" : "bg-[#f0fdf4]"}`}>
            {isDeduction
              ? <ArrowDownCircle className="h-5 w-5 text-[#dc2626]" />
              : <ArrowUpCircle className="h-5 w-5 text-[#16a34a]" />
            }
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <p className="font-semibold text-[#0f172a] truncate">{adj.label}</p>
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${cfg.badge}`}>
                {cfg.label}
              </span>
            </div>
            <p className="text-xs text-[#64748b]">
              {adj.employeeName ?? adj.userId}
              <span className="mx-1.5 text-[#cbd5e1]">·</span>
              {formatPeriod(adj.targetPeriod)}
            </p>
            <p className={`text-sm font-semibold mt-1 ${isDeduction ? "text-[#dc2626]" : "text-[#16a34a]"}`}>
              {isDeduction ? "−" : "+"}{formatCurrency(adj.amount)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Cancel action — only for PENDING */}
          {adj.status === "PENDING" && (
            confirming ? (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => cancel.mutate({ adjId: adj.adjId, userId: adj.userId }, { onSuccess: () => setConfirming(false) })}
                  disabled={cancel.isPending}
                  className="rounded-lg bg-[#dc2626] px-2.5 py-1 text-xs font-medium text-white hover:bg-[#b91c1c] disabled:opacity-60 transition-colors"
                >
                  {cancel.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Yes, cancel"}
                </button>
                <button onClick={() => setConfirming(false)}
                  className="rounded-lg border border-[#e2e8f0] px-2.5 py-1 text-xs font-medium text-[#64748b] hover:bg-[#f8fafc] transition-colors">
                  No
                </button>
              </div>
            ) : (
              <button onClick={() => setConfirming(true)}
                className="rounded-lg border border-[#fecaca] bg-[#fef2f2] px-2.5 py-1 text-xs font-medium text-[#dc2626] hover:bg-[#fee2e2] transition-colors">
                Cancel
              </button>
            )
          )}
          {/* Expand toggle */}
          <button type="button" onClick={() => setExpanded((x) => !x)}
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#e2e8f0] text-[#94a3b8] hover:bg-[#f1f5f9] transition-colors">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-[#f1f5f9] px-5 py-3 bg-[#f8fafc] space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-[#94a3b8]">Employee ID</span>
            <span className="text-[#374151] font-mono text-xs">{adj.userId}</span>
          </div>
          {adj.sourceDate && (
            <div className="flex justify-between">
              <span className="text-[#94a3b8]">Event Date</span>
              <span className="text-[#374151]">{format(new Date(adj.sourceDate), "MMM d, yyyy")}</span>
            </div>
          )}
          {adj.reason && (
            <div className="flex justify-between gap-4">
              <span className="text-[#94a3b8] shrink-0">Reason</span>
              <span className="text-[#374151] text-right">{adj.reason}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-[#94a3b8]">Created by</span>
            <span className="text-[#374151]">{adj.createdBy}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#94a3b8]">Created</span>
            <span className="text-[#374151]">{format(new Date(adj.createdAt), "MMM d, yyyy h:mm a")}</span>
          </div>
          {adj.appliedAt && (
            <div className="flex justify-between">
              <span className="text-[#94a3b8]">Applied</span>
              <span className="text-[#16a34a] font-medium">{format(new Date(adj.appliedAt), "MMM d, yyyy")}</span>
            </div>
          )}
          {adj.cancelledAt && (
            <div className="flex justify-between">
              <span className="text-[#94a3b8]">Cancelled by</span>
              <span className="text-[#dc2626]">
                {adj.cancelledBy} on {format(new Date(adj.cancelledAt), "MMM d, yyyy")}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const STATUS_TABS: { key: AdjustmentStatus | "ALL"; label: string }[] = [
  { key: "ALL",       label: "All" },
  { key: "PENDING",   label: "Pending" },
  { key: "APPLIED",   label: "Applied" },
  { key: "CANCELLED", label: "Cancelled" },
];

export default function AdminAdjustmentsPage() {
  const { activeBusinessId } = useAuth();
  const [period, setPeriod]     = useState(RECENT_PERIODS[0] ?? "");
  const [activeTab, setActiveTab] = useState<AdjustmentStatus | "ALL">("PENDING");
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading, error } = useAdjustments(activeBusinessId, period);
  const all = data?.items ?? [];
  const items = activeTab === "ALL" ? all : all.filter((a) => a.status === activeTab);

  const counts = {
    ALL:       all.length,
    PENDING:   all.filter((a) => a.status === "PENDING").length,
    APPLIED:   all.filter((a) => a.status === "APPLIED").length,
    CANCELLED: all.filter((a) => a.status === "CANCELLED").length,
  };

  return (
    <div className="space-y-5">
      {/* ── Header card ───────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-[#e2e8f0] bg-white px-5 py-4 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#2447d9]">
              <SlidersHorizontal className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-[#0f172a]">Payslip Adjustments</h1>
              <p className="text-xs text-[#64748b]">Prior-period adjustments applied on the next payslip run</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Period selector */}
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-[#64748b] whitespace-nowrap">Period</label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-1.5 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9] focus:border-[#2447d9]"
              >
                {RECENT_PERIODS.map((p) => (
                  <option key={p} value={p}>{formatPeriod(p)}</option>
                ))}
              </select>
            </div>
            {/* New Adjustment button */}
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-1.5 rounded-lg bg-[#2447d9] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Adjustment
            </button>
          </div>
        </div>
      </div>

      {/* ── Status tabs ───────────────────────────────────────────────────── */}
      <div className="flex gap-1 rounded-lg bg-[#f1f5f9] p-1 w-fit overflow-x-auto overflow-y-hidden">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.key
                ? "bg-white text-[#0f172a] shadow-sm"
                : "text-[#64748b] hover:text-[#0f172a]"
            }`}
          >
            {tab.label}
            {counts[tab.key] > 0 && (
              <span className={`text-xs rounded-full px-1.5 py-0.5 font-semibold ${
                activeTab === tab.key
                  ? "bg-[#eff3ff] text-[#2447d9]"
                  : "bg-[#e2e8f0] text-[#64748b]"
              }`}>
                {counts[tab.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" className="text-[#2447d9]" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-[#fecaca] bg-[#fef2f2] px-5 py-4 text-sm text-[#dc2626]">
          Failed to load adjustments. Please try again.
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-[#e2e8f0] bg-white py-20 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#eff3ff]">
            <SlidersHorizontal className="h-7 w-7 text-[#2447d9]" />
          </div>
          <div>
            <p className="font-semibold text-[#0f172a]">No adjustments</p>
            <p className="mt-1 text-sm text-[#94a3b8] max-w-xs">
              {activeTab === "PENDING"
                ? "No pending adjustments for this period. Create one to queue a PPA."
                : `No ${activeTab.toLowerCase()} adjustments for ${formatPeriod(period)}.`}
            </p>
          </div>
          {activeTab === "PENDING" && (
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-1.5 rounded-lg border border-[#c7d2fe] bg-[#eff3ff] px-4 py-2 text-sm font-medium text-[#2447d9] hover:bg-[#e0e7ff] transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Adjustment
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {activeBusinessId && items.map((adj) => (
            <AdjustmentRow key={adj.adjId} adj={adj} businessId={activeBusinessId} />
          ))}
        </div>
      )}

      {/* ── Create Modal ──────────────────────────────────────────────────── */}
      {showCreate && activeBusinessId && (
        <CreateAdjustmentModal
          businessId={activeBusinessId}
          defaultPeriod={period}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
  );
}
